// src/utils/performanceMonitor.js

class PerformanceMonitor {
  constructor() {
    this.enabled = import.meta.env.DEV; // Only enable in development
    this.marks = new Map();
    this.measures = new Map();
    this.renderCounts = new Map();
    this.memorySnapshots = [];
    this.slowOperationThreshold = 16; // One frame at 60fps
    this.memoryCheckInterval = null;
  }

  // Mark a point in time
  mark(name) {
    if (!this.enabled) return;
    
    try {
      performance.mark(name);
      this.marks.set(name, performance.now());
    } catch (error) {
      console.debug('Performance mark failed', error);
    }
  }

  // Measure between two marks
  measure(name, startMark, endMark) {
    if (!this.enabled) return null;
    
    try {
      const measure = performance.measure(name, startMark, endMark);
      const duration = measure.duration;
      
      this.measures.set(name, duration);
      
      if (duration > this.slowOperationThreshold) {
        console.warn(`🐌 Slow operation detected: ${name} took ${duration.toFixed(2)}ms`);
      }
      
      return duration;
    } catch (error) {
      // Fallback to manual calculation
      const start = this.marks.get(startMark);
      const end = this.marks.get(endMark || name);
      
      if (start && end) {
        const duration = end - start;
        this.measures.set(name, duration);
        
        if (duration > this.slowOperationThreshold) {
          console.warn(`🐌 Slow operation detected: ${name} took ${duration.toFixed(2)}ms`);
        }
        
        return duration;
      }
    }
    
    return null;
  }

  // Track component render count
  trackRender(componentName) {
    if (!this.enabled) return;
    
    const count = (this.renderCounts.get(componentName) || 0) + 1;
    this.renderCounts.set(componentName, count);
    
    // Warn about excessive re-renders
    if (count % 50 === 0) {
      console.warn(`⚠️ Component ${componentName} has rendered ${count} times`);
    }
  }

  // Track memory usage
  startMemoryMonitoring(interval = 5000) {
    if (!this.enabled || !performance.memory) return;
    
    this.stopMemoryMonitoring(); // Clear any existing interval
    
    this.memoryCheckInterval = setInterval(() => {
      const snapshot = {
        timestamp: Date.now(),
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      };
      
      this.memorySnapshots.push(snapshot);
      
      // Keep only last 100 snapshots
      if (this.memorySnapshots.length > 100) {
        this.memorySnapshots.shift();
      }
      
      // Check for potential memory leak
      if (this.memorySnapshots.length >= 10) {
        const recentSnapshots = this.memorySnapshots.slice(-10);
        const memoryGrowth = recentSnapshots[9].usedJSHeapSize - recentSnapshots[0].usedJSHeapSize;
        const growthRate = memoryGrowth / (10 * interval) * 1000; // Bytes per second
        
        if (growthRate > 1048576) { // 1MB per second
          console.warn(`⚠️ Potential memory leak detected: ${(growthRate / 1048576).toFixed(2)}MB/s growth`);
        }
      }
      
      // Warn if heap usage is high
      const heapUsagePercent = (snapshot.usedJSHeapSize / snapshot.jsHeapSizeLimit) * 100;
      if (heapUsagePercent > 90) {
        console.error(`🚨 Critical memory usage: ${heapUsagePercent.toFixed(1)}% of heap limit`);
      } else if (heapUsagePercent > 75) {
        console.warn(`⚠️ High memory usage: ${heapUsagePercent.toFixed(1)}% of heap limit`);
      }
    }, interval);
  }

  stopMemoryMonitoring() {
    if (this.memoryCheckInterval) {
      clearInterval(this.memoryCheckInterval);
      this.memoryCheckInterval = null;
    }
  }

  // Get current memory stats
  getMemoryStats() {
    if (!this.enabled || !performance.memory) return null;
    
    return {
      usedMB: (performance.memory.usedJSHeapSize / 1048576).toFixed(2),
      totalMB: (performance.memory.totalJSHeapSize / 1048576).toFixed(2),
      limitMB: (performance.memory.jsHeapSizeLimit / 1048576).toFixed(2),
      percentUsed: ((performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100).toFixed(1)
    };
  }

  // Log all performance data
  logReport() {
    if (!this.enabled) return;
    
    console.group('📊 Performance Report');
    
    // Log measures
    if (this.measures.size > 0) {
      console.group('Operation Timings');
      this.measures.forEach((duration, name) => {
        const emoji = duration > 100 ? '🔴' : duration > 50 ? '🟡' : '🟢';
        console.log(`${emoji} ${name}: ${duration.toFixed(2)}ms`);
      });
      console.groupEnd();
    }
    
    // Log render counts
    if (this.renderCounts.size > 0) {
      console.group('Component Render Counts');
      const sortedRenders = Array.from(this.renderCounts.entries())
        .sort((a, b) => b[1] - a[1]);
      
      sortedRenders.forEach(([name, count]) => {
        const emoji = count > 100 ? '🔴' : count > 50 ? '🟡' : '🟢';
        console.log(`${emoji} ${name}: ${count} renders`);
      });
      console.groupEnd();
    }
    
    // Log memory stats
    const memStats = this.getMemoryStats();
    if (memStats) {
      console.group('Memory Stats');
      console.log(`Used: ${memStats.usedMB} MB (${memStats.percentUsed}%)`);
      console.log(`Total: ${memStats.totalMB} MB`);
      console.log(`Limit: ${memStats.limitMB} MB`);
      console.groupEnd();
    }
    
    console.groupEnd();
  }

  // Clear all data
  clear() {
    this.marks.clear();
    this.measures.clear();
    this.renderCounts.clear();
    this.memorySnapshots = [];
  }

  // Cleanup
  destroy() {
    this.stopMemoryMonitoring();
    this.clear();
    this.enabled = false;
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

// Export singleton
export default performanceMonitor;